/**
 * SessionStorage-backed store for the CHAT search-result generation timestamp
 * (epoch-ms), carried from the in-chat result block into the booking flow.
 *
 * Why a dedicated store (not bookingDetails / global context)?
 * The chat-results 30-min TTL clock is the per-entry `resultsGeneratedAt`
 * (utils/chatResultsExpiry.ts). In chat it lives on the chatData entry, but the
 * booking Review/Traveler pages need to re-check it on-action (until payment).
 * Downstream code paths reset/partial-update bookingDetails, and React-state
 * timing makes a stale-vs-fresh read flaky — so, exactly like
 * `linkCheckpointStore.ts`, this isolates the value in sessionStorage touched
 * only by the chat selection handlers (writer) and the booking-page expiry
 * gates (reader). Separate flight/hotel keys so a flight+hotel cart can gate on
 * either leg independently.
 *
 * Only ever written with a FRESH value: the in-chat expiry gate already blocks
 * selection of a stale result before priming runs, so the timestamp written
 * here is always < 30 min old at write time.
 */

const FLIGHT_KEY = "_zenvoya_chat_flight_results_generated_at";
const HOTEL_KEY = "_zenvoya_chat_hotel_results_generated_at";

function readNumber(key: string): number | undefined {
  try {
    const v = sessionStorage.getItem(key);
    if (!v) return undefined;
    const n = Number(v);
    return Number.isFinite(n) ? n : undefined;
  } catch {
    return undefined;
  }
}

function writeNumber(key: string, value: number): void {
  try {
    sessionStorage.setItem(key, String(value));
  } catch {
    /* private mode / quota — silent */
  }
}

function removeKey(key: string): void {
  try {
    sessionStorage.removeItem(key);
  } catch {
    /* sessionStorage unavailable (SSR / privacy mode) — nothing to clear */
  }
}

export const setChatFlightResultsGeneratedAt = (value: number): void => {
  writeNumber(FLIGHT_KEY, value);
};

export const getChatFlightResultsGeneratedAt = (): number | undefined =>
  readNumber(FLIGHT_KEY);

export const setChatHotelResultsGeneratedAt = (value: number): void => {
  writeNumber(HOTEL_KEY, value);
};

export const getChatHotelResultsGeneratedAt = (): number | undefined =>
  readNumber(HOTEL_KEY);

/** Clears both flight + hotel timestamps (fresh Shop Travel search, post-booking, chat-exit). */
export const clearChatResultsGeneratedAt = (): void => {
  removeKey(FLIGHT_KEY);
  removeKey(HOTEL_KEY);
};
